import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Award, 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Star, 
  Target, 
  TrendingUp, 
  Bell, 
  X,
  Sparkles,
  Trophy,
  Flame,
  Calendar,
  AlertCircle,
  Gift,
  Info,
  Users
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type NotificationType = 
  | 'achievement' 
  | 'progress' 
  | 'milestone' 
  | 'recommendation' 
  | 'reminder' 
  | 'celebration'
  | 'warning'
  | 'info'
  | 'update'
  | 'social'
  | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionLabel?: string;
  actionUrl?: string;
  metadata?: {
    achievementLevel?: number;
    progressPercentage?: number;
    streakCount?: number;
    contentTitle?: string;
    pathName?: string;
    achievementName?: string;
    points?: number;
    category?: string;
  };
}

interface NotificationSystemProps {
  className?: string;
}

export const NotificationSystem: React.FC<NotificationSystemProps> = ({ className }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load notifications from database
  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      // Set up real-time subscription for new notifications
      const subscription = supabase
        .channel('user_notifications')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_notifications',
            filter: `user_id=eq.${user.id}`
          },
          () => {
            loadNotifications();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user?.id]);

  const loadNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Try to get notifications from user_notifications table
      const { data: userNotifications, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error && error.code !== 'PGRST204') {
        console.warn('Notifications table not found, using achievement notifications');
        
        // Fall back to recent achievements as notifications
        const { data: achievements, error: achievementError } = await supabase
          .from('user_achievements')
          .select(`
            id,
            earned_at,
            achievement_definitions!inner(
              name,
              description,
              category,
              points
            )
          `)
          .eq('user_id', user.id)
          .order('earned_at', { ascending: false })
          .limit(10);

        if (achievementError) throw achievementError;

        // Convert achievements to notifications
        const achievementNotifications: Notification[] = achievements?.map(achievement => ({
          id: achievement.id,
          type: 'achievement' as const,
          title: 'Achievement Unlocked!',
          message: `You earned "${achievement.achievement_definitions.name}" (${achievement.achievement_definitions.points} points)`,
          timestamp: new Date(achievement.earned_at),
          read: false,
          metadata: {
            achievementName: achievement.achievement_definitions.name,
            points: achievement.achievement_definitions.points,
            category: achievement.achievement_definitions.category
          }
        })) || [];

        setNotifications(achievementNotifications);
        setUnreadCount(achievementNotifications.length);
      } else {
        // Use actual notifications if table exists
        const mappedNotifications: Notification[] = userNotifications?.map(notif => ({
          id: notif.id,
          type: notif.notification_type || 'system',
          title: notif.title,
          message: notif.message,
          timestamp: new Date(notif.created_at),
          read: notif.read_at !== null,
          actionUrl: notif.action_url,
          metadata: notif.metadata
        })) || [];

        setNotifications(mappedNotifications);
        setUnreadCount(mappedNotifications.filter(n => !n.read).length);
      }
      
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Provide fallback empty state instead of toast error for better UX
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type: NotificationType) => {
    const iconProps = { className: "h-5 w-5" };
    
    switch (type) {
      case 'achievement':
        return <Award {...iconProps} className="h-5 w-5 text-black" />;
      case 'progress':
        return <TrendingUp {...iconProps} className="h-5 w-5 text-black" />;
      case 'milestone':
        return <Trophy {...iconProps} className="h-5 w-5 text-black" />;
      case 'recommendation':
        return <Sparkles {...iconProps} className="h-5 w-5 text-green-600" />;
      case 'reminder':
        return <Clock {...iconProps} className="h-5 w-5 text-black" />;
      case 'celebration':
        return <Gift {...iconProps} className="h-5 w-5 text-pink-600" />;
      case 'warning':
        return <AlertCircle {...iconProps} className="h-5 w-5 text-red-600" />;
      case 'info':
        return <Info {...iconProps} className="h-5 w-5 text-blue-600" />;
      case 'update':
        return <Info {...iconProps} className="h-5 w-5 text-green-600" />;
      case 'social':
        return <Users {...iconProps} className="h-5 w-5 text-purple-600" />;
      case 'system':
        return <AlertCircle {...iconProps} className="h-5 w-5 text-gray-600" />;
      default:
        return <Bell {...iconProps} className="h-5 w-5 text-black" />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'achievement':
        return 'border-sky-200 bg-white';
      case 'progress':
        return 'border-blue-200 bg-white';
      case 'milestone':
        return 'border-purple-200 bg-white';
      case 'recommendation':
        return 'border-green-200 bg-white';
      case 'reminder':
        return 'border-gray-200 bg-white';
      case 'celebration':
        return 'border-pink-200 bg-pink-50';
      case 'warning':
        return 'border-red-200 bg-red-50';
      case 'info':
        return 'border-blue-200 bg-white';
      case 'update':
        return 'border-green-200 bg-white';
      case 'social':
        return 'border-purple-200 bg-white';
      case 'system':
        return 'border-gray-200 bg-white';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      // Try to mark as read in database
      const { error } = await supabase
        .from('user_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error && error.code !== 'PGRST204') {
        console.warn('Could not mark notification as read in database');
      }

      // Update local state regardless
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (user?.id) {
        // Try to mark all as read in database
        const { error } = await supabase
          .from('user_notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .is('read_at', null);

        if (error && error.code !== 'PGRST204') {
          console.warn('Could not mark all notifications as read in database');
        }
      }

      // Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const removeNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, [notifications]);

  const handleNotificationClick = useCallback((notification: Notification) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      // In a real app, this would navigate to the URL
      console.log('Navigate to:', notification.actionUrl);
    }
  }, [markAsRead]);

  // Auto-show achievements and milestones as toasts
  useEffect(() => {
    const recentAchievements = notifications.filter(
      n => (n.type === 'achievement' || n.type === 'milestone') && 
           !n.read && 
           Date.now() - n.timestamp.getTime() < 60000 // Less than 1 minute old
    );

    recentAchievements.forEach(achievement => {
      toast({
        title: achievement.title,
        description: achievement.message,
        duration: 5000,
      });
    });
  }, [notifications, toast]);

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return `${Math.floor(diff / 86400000)} days ago`;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-80 p-0 bg-white border border-gray-200"
      >
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-black">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                Mark all read
              </Button>
            )}
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-600 text-sm">No notifications yet</p>
              <p className="text-gray-500 text-xs mt-1">
                We'll notify you about achievements, reminders, and updates
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 hover:bg-gray-50 cursor-pointer transition-colors",
                    !notification.read && "bg-blue-50 border-l-2 border-l-blue-500"
                  )}
                  onClick={() => {
                    if (!notification.read) {
                      markAsRead(notification.id);
                    }
                    if (notification.actionUrl) {
                      window.location.href = notification.actionUrl;
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-black">
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notification.timestamp.toISOString())}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => {
                setIsOpen(false);
                // Navigate to notifications page when implemented
              }}
            >
              View All Notifications
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Achievement celebration component
export const AchievementCelebration: React.FC<{
  achievement: {
    title: string;
    description: string;
    icon: React.ReactNode;
    level?: number;
    points?: number;
  };
  onClose: () => void;
  show: boolean;
}> = ({ achievement, onClose, show }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-300">
      <Card className="max-w-md w-full mx-4 bg-white border-sky-200">
        <CardContent className="p-6 relative">
          <div className="h-16 w-16 mx-auto bg-white rounded-full flex items-center justify-center">
            <Award className="h-8 w-8 text-black" />
          </div>
          <Sparkles className="absolute -top-1 -right-1 h-6 w-6 text-sky-500" />
          
          <div className="text-center mt-4">
            <h3 className="text-xl font-bold text-black mb-1">
              {achievement.title}
            </h3>
            <p className="text-black">
              {achievement.description}
            </p>
            
            <div className="mt-4 flex justify-center">
              <Badge variant="outline" className="border-sky-300 text-black">
                Philosophy Explorer
              </Badge>
            </div>
          </div>

          {achievement.points && (
            <Badge variant="outline" className="border-sky-300 text-black">
              +{achievement.points} points
            </Badge>
          )}

          <Button onClick={onClose} className="w-full">
            Continue Learning
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Progress milestone celebration
export const MilestoneCelebration: React.FC<{
  milestone: {
    title: string;
    description: string;
    progressPercentage: number;
    pathName?: string;
  };
  onClose: () => void;
  show: boolean;
}> = ({ milestone, onClose, show }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-in fade-in duration-300">
      <Card className="max-w-md w-full mx-4 bg-whiter from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="pt-6 text-center space-y-4">
          <div className="relative">
            <div className="h-16 w-16 mx-auto bg-white rounded-full flex items-center justify-center">
              <Trophy className="h-8 w-8 text-black" />
            </div>
            <div className="absolute -inset-2 bg-white/20 rounded-full animate-pulse" />
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-black mb-1">
              {milestone.title}
            </h3>
            <p className="text-black">
              {milestone.description}
            </p>
            {milestone.pathName && (
              <p className="text-sm text-black mt-1">
                in {milestone.pathName}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-black">Progress</span>
              <span className="font-medium text-black">
                {milestone.progressPercentage}%
              </span>
            </div>
            <div className="h-2 bg-white rounded-full overflow-hidden">
              <div 
                className="h-full bg-white from-purple-500 to-blue-500 transition-all duration-1000"
                style={{ width: `${milestone.progressPercentage}%` }}
              />
            </div>
          </div>

          <Button onClick={onClose} className="w-full bg-purple-600 hover:bg-purple-700">
            Continue Journey
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to trigger notifications
export const useNotifications = () => {
  const { toast } = useToast();

  const triggerAchievement = useCallback((achievement: {
    title: string;
    message: string;
    level?: number;
    points?: number;
  }) => {
    toast({
      title: `🏆 ${achievement.title}`,
      description: achievement.message,
      duration: 5000,
    });
  }, [toast]);

  const triggerProgress = useCallback((progress: {
    title: string;
    message: string;
    percentage?: number;
  }) => {
    toast({
      title: `📈 ${progress.title}`,
      description: progress.message,
      duration: 3000,
    });
  }, [toast]);

  const triggerMilestone = useCallback((milestone: {
    title: string;
    message: string;
    pathName?: string;
  }) => {
    toast({
      title: `🎯 ${milestone.title}`,
      description: milestone.message,
      duration: 4000,
    });
  }, [toast]);

  return {
    triggerAchievement,
    triggerProgress,
    triggerMilestone
  };
}; 